/**
 * A console flow as it is stored (§5.11): the bounds on its canvas and the
 * sanitizer every save goes through — shared, because a project an organization
 * shares carries its flows (§5.25). Running one stays in the desktop app.
 */
import type {
  ApiFlowEdge,
  ApiFlowInput,
  ApiFlowNode,
  ConditionOp,
  ConditionSource,
  ConditionTest,
  FlowCapture,
  FlowCondition
} from './types';
import { isVariableKey, normalizeKey } from './variables';

/** How many a project keeps, and how big one gets — bounds on a canvas, not a quota. */
export const MAX_FLOWS = 40;
export const MAX_FLOW_NAME_CHARS = 60;
export const MAX_FLOW_NODES = 60;
export const MAX_FLOW_EDGES = 120;
/** Per node. More than a handful of names out of one answer is a parser, not a flow. */
export const MAX_NODE_CAPTURES = 8;
export const MAX_CAPTURE_PATH_CHARS = 200;
/** A condition step's guided tests, and the hand-written expression of Advanced (#158). */
export const MAX_CONDITION_TESTS = 6;
export const MAX_CONDITION_VALUE_CHARS = 200;
export const MAX_CONDITION_EXPRESSION_CHARS = 600;

/** The comparisons each source offers — one word, `equals`, for every source that compares. */
export const CONDITION_OPS: Record<ConditionSource, ConditionOp[]> = {
  status: ['is2xx', 'is4xx', 'is5xx', 'equals', 'isNot'],
  body: ['equals', 'isNot', 'moreThan', 'lessThan', 'contains', 'exists'],
  capture: ['exists', 'equals', 'isNot', 'contains'],
  variable: ['exists', 'equals', 'isNot', 'contains']
};

/** The canvas is unbounded in feel and bounded in fact; a position outside this is a bug. */
const MAX_COORD = 100_000;

/** An id minted by the canvas — the same shape the variables editor mints (§5.11). */
const ID = /^[A-Za-z0-9_-]{1,40}$/;

/* ── a flow as it is stored ─────────────────────────────────────────────── */

/**
 * A graph as it will be written (§5.11) — what the canvas sends, held to what a
 * run can actually do. It throws the sentence the user gets back, so the rules
 * are one thing rather than one per surface, exactly as `sanitizeVariables`
 * does for the editor next door.
 *
 * Cycles are *not* refused: a half-drawn graph passes through this on its way to
 * disk, and refusing to save what is on screen would lose it. A cycle is a
 * run-time answer instead — `flowOrder` reports the nodes it cannot reach, and
 * the canvas says so where the user drew them.
 */
export function sanitizeFlow(input: ApiFlowInput): ApiFlowInput {
  if (input.nodes.length > MAX_FLOW_NODES) throw new Error(`A flow holds at most ${MAX_FLOW_NODES} calls.`);
  if (input.edges.length > MAX_FLOW_EDGES) throw new Error(`A flow holds at most ${MAX_FLOW_EDGES} arrows.`);

  const nodes: ApiFlowNode[] = [];
  for (const node of input.nodes) {
    if (!ID.test(node.id)) throw new Error('That canvas sent a node without a usable id.');
    if (nodes.some((n) => n.id === node.id)) throw new Error('That canvas sent two nodes with the same id.');
    if (node.requestId !== null && !ID.test(node.requestId)) {
      throw new Error('That canvas sent a node pointing at nothing recognizable.');
    }
    // A condition step sends nothing and captures nothing: it only asks (#158).
    nodes.push(
      node.kind === 'if'
        ? {
            id: node.id,
            kind: 'if',
            requestId: null,
            x: coord(node.x),
            y: coord(node.y),
            captures: [],
            condition: sanitizeCondition(node.condition)
          }
        : {
            id: node.id,
            requestId: node.requestId,
            x: coord(node.x),
            y: coord(node.y),
            captures: sanitizeCaptures(node.captures)
          }
    );
  }

  const known = new Set(nodes.map((n) => n.id));
  const conditions = new Set(nodes.filter((n) => n.kind === 'if').map((n) => n.id));
  const edges: ApiFlowEdge[] = [];
  for (const edge of input.edges) {
    if (!ID.test(edge.id)) throw new Error('That canvas sent an arrow without a usable id.');
    // An arrow to a node that is gone, or a node onto itself, is not an arrow:
    // dropped rather than refused, since deleting a node is how the first happens.
    if (!known.has(edge.source) || !known.has(edge.target) || edge.source === edge.target) continue;
    if (edges.some((e) => e.source === edge.source && e.target === edge.target)) continue;
    if (edges.some((e) => e.id === edge.id)) throw new Error('That canvas sent two arrows with the same id.');
    // A branch means something only out of a condition step.
    const branch =
      conditions.has(edge.source) && (edge.branch === 'then' || edge.branch === 'else') ? edge.branch : null;
    const arrow: ApiFlowEdge = { id: edge.id, source: edge.source, target: edge.target };
    edges.push(branch ? { ...arrow, branch } : arrow);
  }
  return { nodes, edges };
}

/**
 * The names one node writes. A row with no key yet is dropped rather than
 * refused — an empty line under the last capture is how a form invites the next
 * one — and the key is held to the variables editor's rule, because a capture
 * that cannot be written back as `{{key}}` is a value no call could ever use.
 */
function sanitizeCaptures(captures: FlowCapture[]): FlowCapture[] {
  if (captures.length > MAX_NODE_CAPTURES) throw new Error(`One call yields at most ${MAX_NODE_CAPTURES} values.`);
  const out: FlowCapture[] = [];
  for (const capture of captures) {
    const key = normalizeKey(capture.key);
    if (!key) continue;
    if (!isVariableKey(key)) {
      throw new Error(`“${key}” cannot be written as a variable — use letters, digits, dot, dash or underscore.`);
    }
    if (out.some((c) => c.key === key)) throw new Error(`One call cannot define “${key}” twice.`);
    out.push({
      key,
      from: capture.from === 'header' ? 'header' : capture.from === 'status' ? 'status' : 'body',
      // Capped, not trimmed: the canvas writes while this is being typed and
      // adopts the row that comes back, so trimming it here takes the character
      // just typed back out from under the caret. The run trims what it reads.
      path: capture.path.slice(0, MAX_CAPTURE_PATH_CHARS)
    });
  }
  return out;
}

/**
 * A condition step's question, held to its bounds (#158). It never throws: a
 * condition is written a field at a time and saved as it goes, so what is half
 * written is kept as it stands — the canvas says it is not finished, and a run
 * refuses to start on it. What is not a known source or comparison is put back
 * to one that is.
 */
export function sanitizeCondition(raw: unknown): FlowCondition {
  const input = (typeof raw === 'object' && raw !== null ? raw : {}) as Partial<FlowCondition>;
  const tests = Array.isArray(input.tests) ? input.tests.slice(0, MAX_CONDITION_TESTS) : [];
  return {
    mode: input.mode === 'advanced' ? 'advanced' : 'guided',
    match: input.match === 'any' ? 'any' : 'all',
    tests: tests.map(sanitizeTest),
    expression:
      typeof input.expression === 'string' ? input.expression.slice(0, MAX_CONDITION_EXPRESSION_CHARS) : ''
  };
}

function sanitizeTest(raw: unknown): ConditionTest {
  const input = (typeof raw === 'object' && raw !== null ? raw : {}) as Partial<ConditionTest>;
  const source: ConditionSource =
    input.source === 'body' || input.source === 'capture' || input.source === 'variable' ? input.source : 'status';
  const ops = CONDITION_OPS[source];
  return {
    source,
    node: typeof input.node === 'string' && ID.test(input.node) ? input.node : null,
    path: typeof input.path === 'string' ? input.path.slice(0, MAX_CAPTURE_PATH_CHARS) : '',
    op: input.op && ops.includes(input.op) ? input.op : ops[0]!,
    value: typeof input.value === 'string' ? input.value.slice(0, MAX_CONDITION_VALUE_CHARS) : ''
  };
}

export function coord(value: number): number {
  return Number.isFinite(value) ? Math.round(Math.min(Math.max(value, -MAX_COORD), MAX_COORD)) : 0;
}
